/**
 * Skills Bridge — B2B page scripts (sections/sb-b2b-hero.liquid, sections/sb-b2b-topics.liquid,
 * sections/sb-b2b-consult.liquid, sections/sb-b2b-cases.liquid; also loaded for CTA tracking by sb-b2b-process,
 * sb-b2b-compare, sb-b2b-final-cta, sb-b2b-page-hero).
 *
 * Forms (hero #b2b-form, consult #b2b-form-consult): rendered by Liquid (form_html setting); this file
 * only reports, with sb_section = the wrapper's data-sb-b2b-form value (b2b_hero / b2b_consult):
 * - form_view: the form wrapper enters the viewport (once per page view per wrapper)
 * - form_submit_attempt: a submit event inside the wrapper (AC validates after this fires)
 * - form_submit_success: AC's thank-you message appears in the wrapper (once per wrapper per page view)
 * sb_label = ac_form_<id> from the embed's _form_<id> class. Also marks each wrapper data-sb-form-ready
 * once AC's <form> lands (or after 8s) so the CSS drops the reserved loading height. The consult form's
 * ids get a "--consult" suffix (both AC forms share field ids); the thank-you is scrolled into view.
 * A wrapper showing the consult placeholder skeleton reports nothing.
 *
 * Topics: vertical ARIA tabs from 1000px (logic copied from assets/sb-faq.js — see UI-INVENTORY §9)
 * and an accordion of <details> below 1000px. Reports topic_tab / topic_expand once per group per
 * page view.
 *
 * Links to #b2b-form / #b2b-form-consult, or to any element holding a form wrapper (the consult card's
 * "anchor" setting, e.g. #dang-ky on the coaching page): smooth scroll to it (instant under reduced motion), then
 * focus the form's first field on fine pointers only — on phones focusing opens the keyboard over the form.
 * A link inside an open Impact drawer (sb-trainers profile) closes the drawer first. cta_click goes to the sb_section named by the closest [data-sb-track-section]; links
 * marked [data-sb-track-cta] are reported too, whatever their href. No attribute → no event.
 *
 * Cases: card_click (label = article handle) and cta_click on every click; carousel_nav once per
 * page view.
 *
 * Formats (sections/sb-b2b-formats.liquid): motion only, no events. Skipped under reduced motion or without
 * IntersectionObserver (cards stay visible). [data-sb-motion] hides the cards; .is-in-view fades them up once
 * when the list enters the viewport. A card's icon plays once (data-motion-play) when both its entrance has ended
 * and it is at least half in view, and again on pointerenter / focusin on hover devices (removed + reflow so the
 * CSS animation restarts). CSS in assets/sb-b2b-detail.css. Impact's <reveal-items> is not used: it depends on
 * the theme's "stagger products apparition" setting and has no per-card hook.
 *
 * Uses window.sbTrack from assets/custom.js. custom.js is a module and may run after this
 * deferred script, so the fallback pushes the same sb_interact payload itself.
 * Several sections load this file; the guard keeps it to one run.
 */
(() => {
  if (window.sbB2bLoaded) return;
  window.sbB2bLoaded = true;

  function track(section, action, label) {
    if (typeof window.sbTrack === "function") {
      window.sbTrack(section, action, label);
      return;
    }

    const payload = { event: "sb_interact", sb_section: section, sb_action: action };
    if (label) {
      payload.sb_label = String(label).slice(0, 100);
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  }

  // One event per action + label per page view
  const sent = new Set();
  function trackOnce(section, action, label) {
    const key = `${section}|${action}|${label}`;
    if (sent.has(key)) return;
    sent.add(key);
    track(section, action, label);
  }

  /* ---------- Forms ---------- */

  // "ac_form_128" from the AC wrapper class (_form_128); blank when the embed is not AC.
  function formLabel(wrapper) {
    const match = wrapper.querySelector('[class*="_form_"]')?.className.match(/_form_(\d+)/);
    return match ? `ac_form_${match[1]}` : "";
  }

  // AC's _show_thank_you hides ._form-content and fills + shows ._form-thank-you
  function thankYouShown(wrapper) {
    const thankYou = wrapper.querySelector("._form-thank-you");
    return Boolean(thankYou && thankYou.style.display !== "none" && thankYou.textContent.trim());
  }

  // The card shrinks to the message after submit; bring it back on screen if it ended up outside
  // (below the sticky header = the theme's scroll-padding-top).
  function revealThankYou(wrapper) {
    const thankYou = wrapper.querySelector("._form-thank-you");
    const rect = thankYou.getBoundingClientRect();
    const top = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
    if (rect.top >= top && rect.bottom <= window.innerHeight) return;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    thankYou.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
  }

  // Forms 128 and 130 ship the same field ids (email, phone, field[…], the phone picker's iti-0__…), so
  // a consult label would tick / focus the hero field. The consult copy gets a suffix on every id and on
  // the attributes pointing at them; the hero keeps AC's ids. AC's own ids (_form_<uid>_…) are unique
  // per embed and looked up by AC, so they stay. AC (error tooltips, aria-describedby) and the phone
  // picker (aria-activedescendant) build later references from the element's current id.
  const ID_SUFFIX = { b2b_consult: "--consult" };
  const ID_REFS = ["for", "aria-controls", "aria-describedby", "aria-labelledby", "aria-activedescendant", "aria-owns"];

  function namespaceIds(wrapper, suffix) {
    const ids = new Set();
    wrapper.querySelectorAll("[id]").forEach((el) => {
      if (!el.id) return;
      // includes, not endsWith: AC names error tooltips after the (already suffixed) field id + "-error"
      if (!el.id.startsWith("_form_") && !el.id.includes(suffix)) el.id += suffix;
      ids.add(el.id);
    });

    // AC checkbox ids contain spaces, so a whole-value match comes before the id-list split
    const fix = (value) => (ids.has(value + suffix) ? value + suffix : value);
    wrapper.querySelectorAll(ID_REFS.map((attr) => `[${attr}]`).join(",")).forEach((el) => {
      ID_REFS.forEach((attr) => {
        const value = el.getAttribute(attr);
        if (!value) return;
        const next = ids.has(value + suffix) || attr === "for" ? fix(value) : value.split(/\s+/).map(fix).join(" ");
        if (next !== value) el.setAttribute(attr, next);
      });
    });
  }

  // The CSS reserves the form height until data-sb-form-ready (see "Reserved form height" in sb-b2b.css).
  // Ready = AC has inserted its <form>; after FORM_READY_TIMEOUT the reservation is dropped anyway so a
  // failed embed does not leave a blank card. Then reports form_submit_success once per wrapper.
  const FORM_READY_TIMEOUT = 8000;

  function watchForm(wrapper, section) {
    let succeeded = false;
    const suffix = ID_SUFFIX[section];

    const check = () => {
      const hasForm = Boolean(wrapper.querySelector("form"));
      if (wrapper.dataset.sbFormReady !== "true" && hasForm) {
        wrapper.dataset.sbFormReady = "true";
        window.clearTimeout(timeout);
      }
      // Re-run on every change: the phone picker and AC's error tooltips add ids after the form lands
      if (suffix && hasForm) namespaceIds(wrapper, suffix);
      if (!succeeded && thankYouShown(wrapper)) {
        succeeded = true;
        observer.disconnect();
        track(section, "form_submit_success", formLabel(wrapper));
        revealThankYou(wrapper);
      }
    };

    const timeout = window.setTimeout(() => {
      if (!wrapper.dataset.sbFormReady) wrapper.dataset.sbFormReady = "timeout";
    }, FORM_READY_TIMEOUT);

    // style: AC toggles the thank-you with inline display; childList: the embed, the phone picker, error
    // tooltips and the message land; ID_REFS: references set after a pass get the suffix too
    const observer = new MutationObserver(check);
    observer.observe(wrapper, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: suffix ? ["style", ...ID_REFS] : ["style"],
    });
    check();
  }

  function bindForm(wrapper) {
    if (wrapper.dataset.sbB2bBound) return;
    wrapper.dataset.sbB2bBound = "true";

    // Placeholder skeleton (consult without form_html): no form, no events
    if (wrapper.querySelector("[data-sb-ac-skeleton]")) return;
    const section = wrapper.dataset.sbB2bForm || "b2b_hero";

    watchForm(wrapper, section);

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          track(section, "form_view", formLabel(wrapper));
        },
        { threshold: 0.25 }
      );
      observer.observe(wrapper);
    }

    wrapper.addEventListener("submit", () => track(section, "form_submit_attempt", formLabel(wrapper)), true);
  }

  /* ---------- Topics ---------- */

  function bindTopics(root) {
    if (root.dataset.sbB2bBound) return;
    root.dataset.sbB2bBound = "true";

    const tabs = Array.from(root.querySelectorAll("[data-sb-b2b-tab]"));
    const panelOf = (tab) => document.getElementById(tab.getAttribute("aria-controls"));

    function activate(tab, { moveFocus = false, user = false } = {}) {
      tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute("aria-selected", selected ? "true" : "false");
        t.setAttribute("tabindex", selected ? "0" : "-1");
        const panel = panelOf(t);
        if (panel) panel.hidden = !selected;
      });
      if (moveFocus) tab.focus();
      if (user) trackOnce("b2b_topics", "topic_tab", tab.dataset.sbB2bTopic);
    }

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => activate(tab, { user: true }));

      tab.addEventListener("keydown", (event) => {
        let next = null;
        switch (event.key) {
          case "ArrowDown":
          case "ArrowRight":
            next = tabs[(index + 1) % tabs.length];
            break;
          case "ArrowUp":
          case "ArrowLeft":
            next = tabs[(index - 1 + tabs.length) % tabs.length];
            break;
          case "Home":
            next = tabs[0];
            break;
          case "End":
            next = tabs[tabs.length - 1];
            break;
          default:
            return;
        }
        event.preventDefault();
        activate(next, { moveFocus: true, user: true });
      });
    });

    // Summary click (mouse, Enter, Space) on a closed group = the user expands it.
    // The open-by-default group and theme-editor selections do not count.
    root.querySelectorAll("details[data-sb-b2b-topic] > summary").forEach((summary) => {
      summary.addEventListener("click", () => {
        const details = summary.parentElement;
        if (!details.open) trackOnce("b2b_topics", "topic_expand", details.dataset.sbB2bTopic);
      });
    });

    root.addEventListener("shopify:block:select", (event) => {
      const target = event.target;
      if (target.hasAttribute("data-sb-b2b-tab")) activate(target);
      if (target.tagName === "DETAILS") target.open = true;
    });
  }

  /* ---------- Cases ---------- */

  // carousel_nav is reported once per page view, whichever button comes first.
  let casesNavSent = false;

  function bindCases(root) {
    if (root.dataset.sbB2bBound) return;
    root.dataset.sbB2bBound = "true";

    root.addEventListener("click", (event) => {
      const card = event.target.closest("[data-sb-b2b-case]");
      if (card) {
        track("b2b_cases", "card_click", card.dataset.sbB2bCase);
        return;
      }

      const nav = event.target.closest("[data-sb-b2b-cases-nav]");
      if (nav) {
        if (casesNavSent) return;
        casesNavSent = true;
        track("b2b_cases", "carousel_nav", nav.dataset.sbB2bCasesNav);
        return;
      }

      const cta = event.target.closest("[data-sb-b2b-cases-cta]");
      if (cta) track("b2b_cases", "cta_click", cta.textContent.trim());
    });

    // Tab onto a half-hidden card: the browser's focus scroll loses to scroll-snap and the
    // carousel snaps back, leaving the card mostly off-screen. Select it through the carousel instead.
    const carousel = root.querySelector("scroll-carousel");
    root.addEventListener("focusin", (event) => {
      const item = event.target.closest(".sb-b2b-cases__item");
      if (!item || !carousel || typeof carousel.select !== "function") return;

      const itemRect = item.getBoundingClientRect();
      const areaRect = carousel.getBoundingClientRect();
      if (itemRect.left >= areaRect.left && itemRect.right <= areaRect.right) return;

      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      carousel.select(carousel.items.indexOf(item), { animate: !reduceMotion, force: true });
    });
  }

  /* ---------- Links to the hero form + section CTAs ---------- */

  const FORM_IDS = ["b2b-form", "b2b-form-consult"];
  const FIELD_SELECTOR = 'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';

  function focusFirstField(form) {
    if (!window.matchMedia("(pointer: fine)").matches) return;
    const field = form.querySelector(FIELD_SELECTOR);
    if (field) field.focus({ preventScroll: true });
  }

  // Focus once the scroll has settled; scrollend is missing in older Safari, hence the timeout.
  function afterScroll(callback) {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      window.removeEventListener("scrollend", finish);
      callback();
    };
    window.addEventListener("scrollend", finish, { once: true });
    window.setTimeout(finish, 1000);
  }

  // The element a "#id" link points to, when it is a form wrapper or holds one; null otherwise.
  function formTarget(id) {
    const target = id ? document.getElementById(id) : null;
    if (!target) return null;
    const form = target.matches("[data-sb-b2b-form]") ? target : target.querySelector("[data-sb-b2b-form]");
    return form ? { target, form } : null;
  }

  function scrollToForm({ target, form }) {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
    if (reduceMotion) {
      focusFirstField(form);
    } else {
      afterScroll(() => focusFirstField(form));
    }
  }

  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[href]");
    if (!link) return;

    const href = link.getAttribute("href");
    let id = "";
    if (href.startsWith("#")) {
      try {
        id = decodeURIComponent(href.slice(1));
      } catch (error) {
        id = "";
      }
    }
    const found = formTarget(id);
    const toForm = FORM_IDS.includes(id) || Boolean(found);
    const section = link.closest("[data-sb-track-section]")?.dataset.sbTrackSection;
    if (section && (toForm || link.hasAttribute("data-sb-track-cta"))) {
      track(section, "cta_click", link.textContent.trim());
    }

    if (!found) return;

    event.preventDefault();
    // Impact's drawer locks the page scroll and hands focus back to its opener on a timer after the hide;
    // scrolling on a later timer keeps the page on the form.
    const drawer = link.closest(".drawer");
    if (drawer && drawer.open && typeof drawer.hide === "function") {
      Promise.resolve(drawer.hide()).then(() => {
        window.setTimeout(() => scrollToForm(found), 0);
      });
      return;
    }
    scrollToForm(found);
  });

  /* ---------- Formats ---------- */

  function playFormat(card) {
    card.removeAttribute("data-motion-play");
    void card.offsetWidth;
    card.setAttribute("data-motion-play", "");
  }

  function bindFormats(list) {
    if (list.dataset.sbB2bBound) return;
    list.dataset.sbB2bBound = "true";

    if (!window.matchMedia("(prefers-reduced-motion: no-preference)").matches) return;
    if (!("IntersectionObserver" in window)) return;

    const cards = list.querySelectorAll(".sb-b2b-formats__item");
    list.setAttribute("data-sb-motion", "");

    const listObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        listObserver.disconnect();
        list.classList.add("is-in-view");
      },
      { rootMargin: "0px 0px -15% 0px" }
    );
    listObserver.observe(list);

    // Icon: once both the card's entrance has ended and the card is in view
    const entered = new WeakSet();
    const seen = new WeakSet();
    const playWhenReady = (card) => {
      if (entered.has(card) && seen.has(card)) playFormat(card);
    };

    const cardObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          cardObserver.unobserve(entry.target);
          seen.add(entry.target);
          playWhenReady(entry.target);
        });
      },
      { threshold: 0.5 }
    );

    const canHover = window.matchMedia("(hover: hover)").matches;
    cards.forEach((card) => {
      if (card.dataset.motion === "none") return;
      card.addEventListener("animationend", (event) => {
        if (event.target !== card || event.animationName !== "sb-formats-enter") return;
        entered.add(card);
        playWhenReady(card);
      });
      cardObserver.observe(card);
      if (!canHover) return;
      card.addEventListener("pointerenter", () => playFormat(card));
      card.addEventListener("focusin", () => playFormat(card));
    });
  }

  function init(scope) {
    scope.querySelectorAll("[data-sb-b2b-form]").forEach(bindForm);
    scope.querySelectorAll("[data-sb-b2b-formats]").forEach(bindFormats);
    scope.querySelectorAll("[data-sb-b2b-topics]").forEach(bindTopics);
    scope.querySelectorAll("[data-sb-b2b-cases]").forEach(bindCases);
  }

  init(document);
  document.addEventListener("shopify:section:load", (event) => init(event.target));
})();
